// ── SEQUENCES PAGE ────────────────────────────────────────────────────────────
import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import api from '../utils/api';
import { Card, PageHeader, Btn, Badge, Modal, Input, Select } from '../components/Layout';

export function Sequences() {
  const { company, mode } = useOutletContext();
  const [sequences, setSequences] = useState([]);
  const [loading,   setLoading]   = useState(false);
  const [showNew,   setShowNew]   = useState(false);
  const [generating,setGenerating]= useState(false);
  const [newSeq,    setNewSeq]    = useState({ name: '', type: 'bdm', steps: [] });

  useEffect(() => {
    api.get(`/sequences?company=${encodeURIComponent(company)}`)
      .then(r => setSequences(r.data))
      .catch(() => {});
  }, [company]);

  const generateAI = async () => {
    setGenerating(true);
    try {
      const r = await api.post('/ai/generate-sequence', { type: newSeq.type || mode, company });
      setNewSeq(s => ({ ...s, steps: r.data.steps }));
    } catch { alert('AI generation failed. Check Anthropic API key.'); }
    finally { setGenerating(false); }
  };

  const save = async () => {
    if (!newSeq.name) return alert('Enter a sequence name');
    try {
      const r = await api.post('/sequences', { ...newSeq, company, type: newSeq.type || mode });
      setSequences(s => [r.data, ...s]);
      setShowNew(false);
      setNewSeq({ name: '', type: 'bdm', steps: [] });
    } catch (err) { alert(err.response?.data?.error || 'Failed to save'); }
  };

  const toggleStatus = async seq => {
    const status = seq.status === 'active' ? 'paused' : 'active';
    const r = await api.patch(`/sequences/${seq._id}`, { status });
    setSequences(ss => ss.map(s => s._id === seq._id ? r.data : s));
  };

  const deleteSeq = async id => {
    if (!window.confirm('Delete this sequence?')) return;
    await api.delete(`/sequences/${id}`);
    setSequences(ss => ss.filter(s => s._id !== id));
  };

  const STEP_COLORS = { email: 'var(--accent)', call: 'var(--green)' };

  return (
    <div style={{ padding: 28 }}>
      <PageHeader title="🔁 Sequences" sub="Automated multi-step email + call drip campaigns">
        <Btn primary small onClick={() => setShowNew(true)}>+ New Sequence</Btn>
      </PageHeader>

      {sequences.length === 0 ? (
        <Card style={{ textAlign: 'center', padding: 60 }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🔁</div>
          <div style={{ color: 'var(--muted)', marginBottom: 20 }}>No sequences yet. Create one to automate outreach.</div>
          <Btn primary onClick={() => setShowNew(true)}>Create First Sequence</Btn>
        </Card>
      ) : (
        <div style={{ display: 'grid', gap: 16 }}>
          {sequences.map(seq => (
            <Card key={seq._id}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>{seq.name}</div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <Badge label={seq.type} color={seq.type === 'bdm' ? 'var(--accent)' : 'var(--purple)'} />
                    <Badge label={seq.status} color={seq.status === 'active' ? 'var(--green)' : 'var(--muted)'} />
                    <span style={{ fontSize: 12, color: 'var(--muted)' }}>{seq.steps?.length || 0} steps · {seq.leads?.length || 0} leads</span>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <Btn small onClick={() => toggleStatus(seq)}>
                    {seq.status === 'active' ? '⏸ Pause' : '▶ Activate'}
                  </Btn>
                  <Btn small danger onClick={() => deleteSeq(seq._id)}>🗑</Btn>
                </div>
              </div>

              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                {(seq.steps || []).map((step, i) => (
                  <div key={i} style={{
                    background: 'var(--surface)', border: `1px solid ${STEP_COLORS[step.type] || 'var(--border)'}44`,
                    borderRadius: 7, padding: '8px 14px', fontSize: 12,
                  }}>
                    <span style={{ color: STEP_COLORS[step.type] || 'var(--muted)', fontWeight: 700 }}>Day {step.day}</span>
                    <span style={{ color: 'var(--mid)', marginLeft: 8 }}>{step.type === 'email' ? '✉️' : '📞'} {step.subject?.slice(0, 30)}</span>
                  </div>
                ))}
              </div>
            </Card>
          ))}
        </div>
      )}

      {showNew && (
        <Modal title="✨ New Sequence" onClose={() => setShowNew(false)} width={640}>
          <Input label="Sequence Name" value={newSeq.name} onChange={e => setNewSeq(s => ({ ...s, name: e.target.value }))} placeholder="BDM Outreach — FinTech Q3" />
          <Select label="Type" value={newSeq.type} onChange={e => setNewSeq(s => ({ ...s, type: e.target.value }))}>
            <option value="bdm">BDM (Software Projects)</option>
            <option value="recruiting">Recruiting (C2C/W2)</option>
          </Select>
          <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
            <Btn small onClick={generateAI} disabled={generating}>
              {generating ? '⚡ Generating...' : '🤖 Generate Steps with AI'}
            </Btn>
          </div>

          {newSeq.steps?.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 8 }}>Generated {newSeq.steps.length} steps:</div>
              {newSeq.steps.map((step, i) => (
                <div key={i} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 7, padding: '10px 14px', marginBottom: 6, fontSize: 12 }}>
                  <span style={{ color: 'var(--accent)', fontWeight: 700 }}>Day {step.day}</span>
                  <span style={{ color: 'var(--mid)', marginLeft: 8 }}>{step.type === 'email' ? '✉️' : '📞'} {step.subject}</span>
                </div>
              ))}
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <Btn small onClick={() => setShowNew(false)}>Cancel</Btn>
            <Btn primary small onClick={save}>Save Sequence</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── PIPELINE PAGE ─────────────────────────────────────────────────────────────
export function Pipeline() {
  const { company, mode } = useOutletContext();
  const [deals,   setDeals]   = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form,    setForm]    = useState({ title: '', leadName: '', value: '', stage: 'new', notes: '' });

  const BDM_STAGES  = ['new','emailed','called','replied','demo_booked','proposal_sent','won','lost'];
  const REC_STAGES  = ['new','emailed','bench_confirmed','resume_received','submitted','placed'];
  const stages = mode === 'bdm' ? BDM_STAGES : REC_STAGES;
  const STAGE_COLORS = { new: 'var(--muted)', emailed: 'var(--amber)', called: 'var(--green)', replied: 'var(--accent)', demo_booked: 'var(--purple)', proposal_sent: '#ff6b6b', won: 'var(--green)', lost: 'var(--red)', bench_confirmed: 'var(--amber)', resume_received: 'var(--accent)', submitted: 'var(--purple)', placed: 'var(--green)' };

  useEffect(() => {
    api.get(`/pipeline?company=${encodeURIComponent(company)}&type=${mode}`)
      .then(r => setDeals(r.data)).catch(() => {});
  }, [company, mode]);

  const addDeal = async () => {
    if (!form.title) return;
    const r = await api.post('/pipeline', { ...form, company, type: mode, value: Number(form.value) || 0 });
    setDeals(d => [...d, r.data]);
    setShowAdd(false);
    setForm({ title: '', leadName: '', value: '', stage: 'new', notes: '' });
  };

  const updateStage = async (deal, stage) => {
    const r = await api.patch(`/pipeline/${deal._id}`, { stage });
    setDeals(ds => ds.map(d => d._id === deal._id ? r.data : d));
  };

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  return (
    <div style={{ padding: 28 }}>
      <PageHeader title="📊 Pipeline" sub={`${deals.length} deals · ${company}`}>
        <Btn primary small onClick={() => setShowAdd(true)}>+ Add Deal</Btn>
      </PageHeader>

      <div style={{ overflowX: 'auto', paddingBottom: 8 }}>
        <div style={{ display: 'flex', gap: 14, minWidth: stages.length * 200 }}>
          {stages.map(stage => {
            const stageDeals = deals.filter(d => d.stage === stage);
            const color = STAGE_COLORS[stage] || 'var(--muted)';
            return (
              <div key={stage} style={{ flex: '0 0 200px' }}>
                <div style={{ fontSize: 11, color, fontWeight: 700, textTransform: 'uppercase', letterSpacing: .5, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                  {stage.replace(/_/g, ' ')}
                  <span style={{ background: color + '22', borderRadius: 10, padding: '1px 7px', fontSize: 11 }}>{stageDeals.length}</span>
                </div>
                {stageDeals.map(deal => (
                  <div key={deal._id} style={{ background: 'var(--card)', border: `1px solid var(--border)`, borderLeft: `3px solid ${color}`, borderRadius: 8, padding: '12px 14px', marginBottom: 10 }}>
                    <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>{deal.title}</div>
                    {deal.leadName && <div style={{ color: 'var(--muted)', fontSize: 12, marginBottom: 6 }}>👤 {deal.leadName}</div>}
                    {deal.value > 0 && <div style={{ color: 'var(--green)', fontSize: 12, fontWeight: 700 }}>${deal.value.toLocaleString()}</div>}
                    <div style={{ marginTop: 10 }}>
                      <select value={deal.stage} onChange={e => updateStage(deal, e.target.value)} style={{ width: '100%', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 5, padding: '4px 8px', color: color, fontSize: 11, cursor: 'pointer', fontWeight: 600 }}>
                        {stages.map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
                      </select>
                    </div>
                  </div>
                ))}
                {stageDeals.length === 0 && (
                  <div style={{ border: '1px dashed var(--border)', borderRadius: 8, padding: 20, color: 'var(--muted)', fontSize: 12, textAlign: 'center' }}>Empty</div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {showAdd && (
        <Modal title="➕ Add Deal" onClose={() => setShowAdd(false)}>
          <Input label="Deal Title *" value={form.title} onChange={set('title')} placeholder="Website Redesign — Acme Corp" />
          <Input label="Lead / Contact Name" value={form.leadName} onChange={set('leadName')} />
          <Input label="Value ($)" value={form.value} onChange={set('value')} type="number" placeholder="0" />
          <Select label="Stage" value={form.stage} onChange={set('stage')}>
            {stages.map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
          </Select>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <Btn small onClick={() => setShowAdd(false)}>Cancel</Btn>
            <Btn primary small onClick={addDeal}>Add Deal</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── AI ASSISTANT PAGE ─────────────────────────────────────────────────────────
export function AIAssistant() {
  const { company } = useOutletContext();
  const [messages, setMessages] = useState([]);
  const [input,    setInput]    = useState('');
  const [loading,  setLoading]  = useState(false);

  const SUGGESTIONS = [
    'Write a follow-up email for a lead who didn\'t reply',
    'How do I find more C2C bench consultants faster?',
    'Best email subject lines for BDM outreach',
    'How to handle "we already have a vendor" objection',
    'Create a 5-day follow-up strategy for warm leads',
    'What should I say on a cold call to a CTO?',
  ];

  const send = async (msg = input) => {
    if (!msg.trim()) return;
    const userMsg = { role: 'user', content: msg };
    setMessages(m => [...m, userMsg]);
    setInput('');
    setLoading(true);
    try {
      const history = messages.slice(-10).map(m => ({ role: m.role, content: m.content }));
      const r = await api.post('/ai/chat', { message: msg, company, history });
      setMessages(m => [...m, { role: 'assistant', content: r.data.reply }]);
    } catch {
      setMessages(m => [...m, { role: 'assistant', content: 'AI temporarily unavailable. Check your Anthropic API key in Settings.' }]);
    } finally { setLoading(false); }
  };

  return (
    <div style={{ padding: 28, height: 'calc(100vh - 0px)', display: 'flex', flexDirection: 'column' }}>
      <PageHeader title="🤖 AI Assistant" sub={`Ask anything about sales, recruiting, or strategy · ${company}`} />

      <Card style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: 0 }}>
        {/* Messages */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
          {messages.length === 0 && (
            <div style={{ textAlign: 'center', paddingTop: 40 }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>🤖</div>
              <div style={{ color: 'var(--muted)', fontSize: 14, marginBottom: 24 }}>
                Your AI sales strategist for {company}.<br/>Ask about emails, calls, objections, or strategy.
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center' }}>
                {SUGGESTIONS.map(s => (
                  <button key={s} onClick={() => send(s)} style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--mid)', borderRadius: 20, padding: '7px 14px', fontSize: 12, cursor: 'pointer' }}>{s}</button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} style={{ alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start', maxWidth: '78%' }}>
              <div style={{
                background: m.role === 'user' ? 'linear-gradient(135deg, var(--accent)33, #7c3aed33)' : 'var(--surface)',
                border: `1px solid ${m.role === 'user' ? 'var(--accent)44' : 'var(--border)'}`,
                borderRadius: m.role === 'user' ? '14px 14px 2px 14px' : '14px 14px 14px 2px',
                padding: '12px 16px', fontSize: 14, lineHeight: 1.7, color: 'var(--text)',
                whiteSpace: 'pre-wrap',
              }}>{m.content}</div>
            </div>
          ))}
          {loading && <div style={{ color: 'var(--accent)', fontSize: 13 }}>⚡ Thinking...</div>}
        </div>

        {/* Input */}
        <div style={{ borderTop: '1px solid var(--border)', padding: '14px 16px', display: 'flex', gap: 10 }}>
          <input
            value={input} onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && !e.shiftKey && !loading && send()}
            placeholder={`Ask about ${company} sales strategy, email scripts, recruiting...`}
            style={{ flex: 1, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 14px', color: 'var(--text)', fontSize: 13, outline: 'none', fontFamily: 'inherit' }}
          />
          <Btn primary onClick={() => send()} disabled={loading || !input.trim()}>Send</Btn>
        </div>
      </Card>
    </div>
  );
}

// ── SETTINGS PAGE ─────────────────────────────────────────────────────────────
export function Settings() {
  const { company } = useOutletContext();
  const [tab,     setTab]     = useState('integrations');
  const [config,  setConfig]  = useState(null);
  const [saving,  setSaving]  = useState(false);
  const [saved,   setSaved]   = useState(false);

  useEffect(() => {
    api.get(`/company-configs/${encodeURIComponent(company)}`)
      .then(r => setConfig(r.data))
      .catch(() => setConfig({ company, focus: ['bdm', 'recruiting'], targetIndustries: [], targetTitles: [], targetSkills: [], emailDailyLimit: 100, callDailyLimit: 50, whatsappDailyLimit: 50 }));
  }, [company]);

  const saveConfig = async () => {
    setSaving(true);
    try {
      await api.put(`/company-configs/${encodeURIComponent(company)}`, config);
      setSaved(true); setTimeout(() => setSaved(false), 2000);
    } catch (err) { alert(err.response?.data?.error || 'Save failed'); }
    finally { setSaving(false); }
  };

  const setC = k => e => setConfig(c => ({ ...c, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
  const setArr = (k, val) => setConfig(c => ({ ...c, [k]: val.split(',').map(v => v.trim()).filter(Boolean) }));

  const Section = ({ title, children }) => (
    <Card style={{ marginBottom: 20 }}>
      <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 20, paddingBottom: 12, borderBottom: '1px solid var(--border)' }}>{title}</div>
      {children}
    </Card>
  );

  const TABS = [
    { id: 'company',      label: '🏢 Company Profile' },
    { id: 'integrations', label: '🔑 API Keys' },
    { id: 'whatsapp',     label: '💬 WhatsApp' },
    { id: 'meta',         label: '📘 Meta Ads' },
    { id: 'google',       label: '🔵 Google Ads' },
    { id: 'aicalling',    label: '🤖 AI Calling' },
  ];

  return (
    <div style={{ padding: 28 }}>
      <PageHeader title="⚙️ Settings" sub={`Configuration for ${company}`} />

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 24, flexWrap: 'wrap' }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            padding: '8px 14px', borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer',
            background: tab === t.id ? 'var(--accent)' : 'var(--surface)',
            color:      tab === t.id ? '#000' : 'var(--muted)',
            border: `1px solid ${tab === t.id ? 'var(--accent)' : 'var(--border)'}`,
          }}>{t.label}</button>
        ))}
      </div>

      {/* ── Company Profile ── */}
      {tab === 'company' && config && (
        <>
          <div style={{ background: 'var(--accent)11', border: '1px solid var(--accent)33', borderRadius: 8, padding: '12px 16px', marginBottom: 20, fontSize: 13, color: 'var(--accent)' }}>
            💡 Company profile controls ICP targeting, mode availability, and daily limits for {company}.
          </div>
          <Section title={`🏢 ${company} — Ideal Customer Profile`}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Input label="Website" value={config.website || ''} onChange={setC('website')} placeholder="https://example.com" />
              <Input label="Tagline" value={config.tagline || ''} onChange={setC('tagline')} placeholder="What you do in 1 sentence" />
              <Input label="Calendly / Booking URL" value={config.calendlyUrl || ''} onChange={setC('calendlyUrl')} placeholder="https://calendly.com/..." />
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: .5, marginBottom: 5 }}>ICP Notes (for AI context)</label>
              <textarea value={config.icpNotes || ''} onChange={setC('icpNotes')} rows={3}
                placeholder="Describe your ideal customer — company size, budget, pain points..."
                style={{ width: '100%', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 7, padding: '10px 12px', color: 'var(--text)', fontSize: 13, resize: 'vertical', fontFamily: 'inherit' }} />
            </div>
          </Section>
          <Section title="🎯 BDM Targeting">
            <Input label="Target Industries (comma-separated)" value={(config.targetIndustries || []).join(', ')} onChange={e => setArr('targetIndustries', e.target.value)} placeholder="FinTech, SaaS, Healthcare, E-Commerce" />
            <Input label="Target Job Titles (comma-separated)" value={(config.targetTitles || []).join(', ')} onChange={e => setArr('targetTitles', e.target.value)} placeholder="CTO, VP Engineering, IT Director" />
          </Section>
          <Section title="👔 Recruiting Targeting">
            <Input label="Target Skills (comma-separated)" value={(config.targetSkills || []).join(', ')} onChange={e => setArr('targetSkills', e.target.value)} placeholder="Java, Salesforce, SAP, Python, AWS" />
          </Section>
          <Section title="⚙️ Daily Limits">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
              <Input label="Email limit / day" value={config.emailDailyLimit || 100} onChange={setC('emailDailyLimit')} type="number" />
              <Input label="Call limit / day" value={config.callDailyLimit || 50} onChange={setC('callDailyLimit')} type="number" />
              <Input label="WhatsApp limit / day" value={config.whatsappDailyLimit || 50} onChange={setC('whatsappDailyLimit')} type="number" />
            </div>
          </Section>
          <Btn primary onClick={saveConfig} disabled={saving}>{saved ? '✅ Saved!' : saving ? 'Saving...' : 'Save Company Profile'}</Btn>
        </>
      )}

      {/* ── API Keys / Integrations ── */}
      {tab === 'integrations' && (
        <>
          <div style={{ background: 'var(--amber)18', border: '1px solid var(--amber)44', borderRadius: 8, padding: '12px 16px', marginBottom: 24, fontSize: 13, color: 'var(--amber)' }}>
            ⚠️ API keys live in the <strong>.env</strong> file on your server for security. Restart the server after any change.
          </div>
          <Section title="🤖 Anthropic Claude API">
            <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 12 }}>Powers AI email writing, call simulation, sequence generation, and the AI chat assistant.</div>
            <Input label="ANTHROPIC_API_KEY" value="sk-ant-••••••••••••" disabled style={{ opacity: .6 }} />
            <Input label="AI_MODEL (optional override)" value="claude-sonnet-4-20250514" disabled style={{ opacity: .6 }} />
            <a href="https://console.anthropic.com" target="_blank" rel="noreferrer" style={{ fontSize: 12, color: 'var(--accent)' }}>→ Get key from console.anthropic.com</a>
          </Section>
          <Section title="🎯 Apollo.io">
            <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 12 }}>275M+ B2B contacts. Powers the Lead → Apollo Search feature.</div>
            <Input label="APOLLO_API_KEY" value="••••••••••••" disabled style={{ opacity: .6 }} />
            <a href="https://app.apollo.io/#/settings/integrations/api" target="_blank" rel="noreferrer" style={{ fontSize: 12, color: 'var(--accent)' }}>→ Apollo Settings → Integrations → API</a>
          </Section>
          <Section title="📊 HubSpot CRM">
            <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 12 }}>Syncs leads and deals to HubSpot pipelines. Use a Private App token (not an API key).</div>
            <Input label="HUBSPOT_API_KEY (Private App Token)" value="••••••••••••" disabled style={{ opacity: .6 }} />
            <a href="https://app.hubspot.com/private-apps" target="_blank" rel="noreferrer" style={{ fontSize: 12, color: 'var(--accent)' }}>→ HubSpot → Settings → Private Apps → Create</a>
          </Section>
          <Section title="⚡ Instantly.ai">
            <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 12 }}>High-volume cold email with domain warm-up, deliverability scoring, and reply tracking.</div>
            <Input label="INSTANTLY_API_KEY" value="••••••••••••" disabled style={{ opacity: .6 }} />
            <a href="https://app.instantly.ai/app/settings/integrations" target="_blank" rel="noreferrer" style={{ fontSize: 12, color: 'var(--accent)' }}>→ Instantly → Settings → Integrations</a>
          </Section>
          <Section title="📧 Gmail SMTP">
            <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 12 }}>Direct email sending. Use an App Password — never your real Gmail password.</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Input label="SMTP_USER (Gmail)" value="your@gmail.com" disabled style={{ opacity: .6 }} />
              <Input label="SMTP_PASS (App Password)" value="••••••••••••" disabled style={{ opacity: .6 }} />
            </div>
            <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer" style={{ fontSize: 12, color: 'var(--accent)' }}>→ Google Account → Security → App Passwords</a>
          </Section>
          <Section title="📋 Calendly (Scheduling)">
            <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 12 }}>Add your Calendly link to emails so leads can book calls directly. No API key needed — paste the URL into Company Profile above.</div>
            <a href="https://calendly.com" target="_blank" rel="noreferrer" style={{ fontSize: 12, color: 'var(--accent)' }}>→ Create free Calendly account</a>
          </Section>
          <Card>
            <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 10 }}>📋 Full .env template</div>
            <pre style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, padding: 16, fontSize: 11, color: 'var(--mid)', overflowX: 'auto', lineHeight: 1.9 }}>
{`PORT=5000
MONGO_URI=mongodb://localhost:27017/autopilot
JWT_SECRET=change_this_32char_random_string
AI_MODEL=claude-sonnet-4-20250514
ANTHROPIC_API_KEY=sk-ant-...
APOLLO_API_KEY=...
HUBSPOT_API_KEY=...
INSTANTLY_API_KEY=...
SMTP_HOST=smtp.gmail.com
SMTP_PORT=587
SMTP_USER=your@gmail.com
SMTP_PASS=your_app_password
VAPI_API_KEY=...
VAPI_PHONE_ID=...
WHATSAPP_PHONE_ID=...
WHATSAPP_TOKEN=...
WHATSAPP_VERIFY_TOKEN=autopilot_verify_2024
META_ACCESS_TOKEN=...
META_AD_ACCOUNT_ID=...
META_PAGE_ID=...
META_VERIFY_TOKEN=autopilot_meta_verify
GOOGLE_ADS_DEVELOPER_TOKEN=...
GOOGLE_ADS_CUSTOMER_ID=...
GOOGLE_ADS_ACCESS_TOKEN=...`}
            </pre>
          </Card>
        </>
      )}

      {/* ── WhatsApp ── */}
      {tab === 'whatsapp' && (
        <>
          <div style={{ background: '#25D36618', border: '1px solid #25D36644', borderRadius: 8, padding: '12px 16px', marginBottom: 20, fontSize: 13, color: '#25D366' }}>
            💬 WhatsApp Business API lets you send follow-up messages directly from the 💬 button on any lead.
          </div>
          <Section title="💬 WhatsApp Business API Setup">
            <div style={{ fontSize: 13, color: 'var(--mid)', lineHeight: 1.9, marginBottom: 16 }}>
              <strong>Step 1</strong> — Go to <a href="https://developers.facebook.com/apps/" target="_blank" rel="noreferrer" style={{ color: '#25D366' }}>Meta Developers → Create App → Business</a><br/>
              <strong>Step 2</strong> — Add the <strong>WhatsApp</strong> product to your app<br/>
              <strong>Step 3</strong> — Get your <strong>Phone Number ID</strong> from WhatsApp → API Setup<br/>
              <strong>Step 4</strong> — Generate a <strong>System User Access Token</strong> (permanent, not temp)<br/>
              <strong>Step 5</strong> — Add to .env: <code style={{ background: 'var(--surface)', padding: '1px 5px', borderRadius: 3 }}>WHATSAPP_PHONE_ID</code> and <code style={{ background: 'var(--surface)', padding: '1px 5px', borderRadius: 3 }}>WHATSAPP_TOKEN</code>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Input label="WHATSAPP_PHONE_ID" value="••••••••••••" disabled style={{ opacity: .6 }} />
              <Input label="WHATSAPP_TOKEN" value="••••••••••••" disabled style={{ opacity: .6 }} />
              <Input label="WHATSAPP_VERIFY_TOKEN (for webhook)" value="autopilot_whatsapp_verify_2024" disabled style={{ opacity: .6 }} />
            </div>
          </Section>
          <Section title="🔗 Webhook Configuration">
            <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 12 }}>Set up this webhook in your Meta App to receive incoming WhatsApp replies:</div>
            <div style={{ background: 'var(--surface)', borderRadius: 7, padding: '12px 16px', fontSize: 12, fontFamily: 'monospace', color: 'var(--mid)', marginBottom: 8 }}>
              Callback URL: https://your-domain.com/api/whatsapp/webhook<br/>
              Verify Token: autopilot_whatsapp_verify_2024<br/>
              Fields to subscribe: messages
            </div>
            <div style={{ fontSize: 12, color: 'var(--muted)' }}>Incoming replies auto-update lead status to "replied" and log to Activity.</div>
          </Section>
          <Section title="✅ Pre-approved Templates">
            <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 12 }}>WhatsApp requires pre-approved message templates for the first outreach (24h window). Use the Template Library to manage them. Templates must be approved in Meta Business Manager.</div>
            <a href="https://business.facebook.com/wa/manage/message-templates/" target="_blank" rel="noreferrer" style={{ fontSize: 12, color: '#25D366' }}>→ Manage WhatsApp Templates in Meta Business Manager</a>
          </Section>
        </>
      )}

      {/* ── Meta Ads ── */}
      {tab === 'meta' && (
        <>
          <div style={{ background: '#1877F218', border: '1px solid #1877F244', borderRadius: 8, padding: '12px 16px', marginBottom: 20, fontSize: 13, color: '#1877F2' }}>
            📘 Meta Ads API connects Facebook/Instagram campaigns and auto-imports leads from Lead Forms directly into your lead database.
          </div>
          <Section title="🔑 Meta API Setup">
            <div style={{ fontSize: 13, color: 'var(--mid)', lineHeight: 1.9, marginBottom: 16 }}>
              <strong>Step 1</strong> — <a href="https://developers.facebook.com/apps/" target="_blank" rel="noreferrer" style={{ color: '#1877F2' }}>Create Meta App</a> → Business type<br/>
              <strong>Step 2</strong> — Add <strong>Marketing API</strong> product<br/>
              <strong>Step 3</strong> — Create a <strong>System User</strong> in Business Manager → assign Ad Account access<br/>
              <strong>Step 4</strong> — Generate permanent access token for the System User<br/>
              <strong>Step 5</strong> — Add <code style={{ background: 'var(--surface)', padding: '1px 4px', borderRadius: 3 }}>META_ACCESS_TOKEN</code>, <code style={{ background: 'var(--surface)', padding: '1px 4px', borderRadius: 3 }}>META_AD_ACCOUNT_ID</code>, <code style={{ background: 'var(--surface)', padding: '1px 4px', borderRadius: 3 }}>META_PAGE_ID</code> to .env
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Input label="META_ACCESS_TOKEN (System User)" value="••••••••••••" disabled style={{ opacity: .6 }} />
              <Input label="META_AD_ACCOUNT_ID (act_XXXXX)" value="••••••••••••" disabled style={{ opacity: .6 }} />
              <Input label="META_PAGE_ID (Facebook Page)" value="••••••••••••" disabled style={{ opacity: .6 }} />
              <Input label="META_VERIFY_TOKEN (for webhook)" value="autopilot_meta_verify_2024" disabled style={{ opacity: .6 }} />
            </div>
          </Section>
          <Section title="📋 Company-wise Ad Strategy">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {[
                { co: 'Maven Group Global', color: '#00d4ff', strategy: 'LinkedIn + Meta targeting US CTOs and VP Engineering at 50-500 person companies. Objective: Lead Generation. Budget: $20-50/day. Use retargeting for website visitors.' },
                { co: 'B2techsoftLLC',      color: '#7c3aed', strategy: 'Split budget: 50% BDM (IT Directors at mid-market), 50% Recruiting (LinkedIn targeting IT staffing firms). Use lookalike audiences from HubSpot contacts.' },
                { co: 'Xolosoft LLC',       color: '#00e676', strategy: 'Meta targeting IT consultants, freelancers, and H1B holders looking for new projects. Use job title targeting: "IT Consultant", "Java Developer", "SAP Consultant".' },
              ].map(item => (
                <div key={item.co} style={{ background: 'var(--surface)', borderRadius: 8, padding: 14, borderLeft: `3px solid ${item.color}` }}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: item.color, marginBottom: 6 }}>{item.co}</div>
                  <div style={{ fontSize: 12, color: 'var(--mid)', lineHeight: 1.7 }}>{item.strategy}</div>
                </div>
              ))}
            </div>
          </Section>
          <Section title="🔗 Lead Form Webhook">
            <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 8 }}>Set up in Meta App → Webhooks → Page → leadgen field:</div>
            <div style={{ background: 'var(--surface)', borderRadius: 7, padding: '12px 16px', fontSize: 12, fontFamily: 'monospace', color: 'var(--mid)' }}>
              Callback URL: https://your-domain.com/api/meta/webhook<br/>
              Verify Token: autopilot_meta_verify_2024<br/>
              Fields: leadgen
            </div>
          </Section>
        </>
      )}

      {/* ── Google Ads ── */}
      {tab === 'google' && (
        <>
          <div style={{ background: '#4285F418', border: '1px solid #4285F444', borderRadius: 8, padding: '12px 16px', marginBottom: 20, fontSize: 13, color: '#4285F4' }}>
            🔵 Google Ads API enables Search and Display campaigns. Generate campaign specs from the UI and upload via Google Ads Editor or the API.
          </div>
          <Section title="🔑 Google Ads API Setup">
            <div style={{ fontSize: 13, color: 'var(--mid)', lineHeight: 1.9, marginBottom: 16 }}>
              <strong>Step 1</strong> — <a href="https://developers.google.com/google-ads/api/docs/start" target="_blank" rel="noreferrer" style={{ color: '#4285F4' }}>Apply for Developer Token</a> (Google Ads API Center)<br/>
              <strong>Step 2</strong> — Create OAuth2 credentials in Google Cloud Console<br/>
              <strong>Step 3</strong> — Get a refresh token using the OAuth2 flow<br/>
              <strong>Step 4</strong> — Add to .env: <code style={{ background: 'var(--surface)', padding: '1px 4px', borderRadius: 3 }}>GOOGLE_ADS_DEVELOPER_TOKEN</code>, <code style={{ background: 'var(--surface)', padding: '1px 4px', borderRadius: 3 }}>GOOGLE_ADS_CUSTOMER_ID</code>, <code style={{ background: 'var(--surface)', padding: '1px 4px', borderRadius: 3 }}>GOOGLE_ADS_ACCESS_TOKEN</code>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Input label="GOOGLE_ADS_DEVELOPER_TOKEN" value="••••••••••••" disabled style={{ opacity: .6 }} />
              <Input label="GOOGLE_ADS_CUSTOMER_ID (XXX-XXX-XXXX)" value="••••••••••••" disabled style={{ opacity: .6 }} />
              <Input label="GOOGLE_ADS_ACCESS_TOKEN (OAuth)" value="••••••••••••" disabled style={{ opacity: .6 }} />
              <Input label="GOOGLE_ADS_MANAGER_ID (optional)" value="••••••••••••" disabled style={{ opacity: .6 }} />
            </div>
          </Section>
          <Section title="📋 Company-wise Google Ads Strategy">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {[
                { co: 'Maven Group Global', color: '#00d4ff', strategy: 'Search: "offshore software development", "hire React developers", "custom software company USA". Target CPC $3-8. Landing page: Discovery call booking.', keywords: ['offshore software development', 'custom software development USA', 'hire dedicated developers'] },
                { co: 'B2techsoftLLC',      color: '#7c3aed', strategy: 'Search: "SAP consultant hire", "Salesforce implementation partner", "IT staffing agency". Display: retarget LinkedIn visitors. Budget: $30/day.', keywords: ['SAP FICO consultant', 'Salesforce developer hire', 'enterprise IT staffing'] },
                { co: 'Xolosoft LLC',       color: '#00e676', strategy: 'Search: "IT consulting C2C jobs", "Java developer contract", "remote Python contract". Target consultants actively searching. CPC $2-5.', keywords: ['IT consultant C2C', 'Python developer contract remote', 'AWS architect contract job'] },
              ].map(item => (
                <div key={item.co} style={{ background: 'var(--surface)', borderRadius: 8, padding: 14, borderLeft: `3px solid ${item.color}` }}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: item.color, marginBottom: 6 }}>{item.co}</div>
                  <div style={{ fontSize: 12, color: 'var(--mid)', lineHeight: 1.7, marginBottom: 8 }}>{item.strategy}</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {item.keywords.map(k => <span key={k} style={{ background: item.color + '22', color: item.color, border: `1px solid ${item.color}44`, borderRadius: 4, padding: '2px 8px', fontSize: 11 }}>{k}</span>)}
                  </div>
                </div>
              ))}
            </div>
          </Section>
        </>
      )}

      {/* ── AI Calling ── */}
      {tab === 'aicalling' && (
        <>
          <div style={{ background: 'var(--accent)18', border: '1px solid var(--accent)44', borderRadius: 8, padding: '12px 16px', marginBottom: 20, fontSize: 13, color: 'var(--accent)' }}>
            🤖 AI Calling uses Vapi.ai to make real outbound phone calls powered by Claude. Without a Vapi key, the 🤖📞 button runs a simulation and generates a synthetic transcript.
          </div>
          <Section title="📞 Vapi.ai — Real AI Phone Calls">
            <div style={{ fontSize: 13, color: 'var(--mid)', lineHeight: 1.9, marginBottom: 16 }}>
              <strong>Step 1</strong> — <a href="https://vapi.ai" target="_blank" rel="noreferrer" style={{ color: 'var(--accent)' }}>Create Vapi.ai account</a> (free tier: 10 minutes/month)<br/>
              <strong>Step 2</strong> — Buy a phone number in Vapi dashboard ($5-10/month)<br/>
              <strong>Step 3</strong> — Copy your API Key from Vapi dashboard<br/>
              <strong>Step 4</strong> — Add to .env: <code style={{ background: 'var(--surface)', padding: '1px 4px', borderRadius: 3 }}>VAPI_API_KEY</code>, <code style={{ background: 'var(--surface)', padding: '1px 4px', borderRadius: 3 }}>VAPI_PHONE_ID</code><br/>
              <strong>Step 5</strong> — Set webhook in Vapi: <code style={{ background: 'var(--surface)', padding: '1px 4px', borderRadius: 3 }}>https://your-domain.com/api/ai/call-webhook</code>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Input label="VAPI_API_KEY" value="••••••••••••" disabled style={{ opacity: .6 }} />
              <Input label="VAPI_PHONE_ID (phone number ID)" value="••••••••••••" disabled style={{ opacity: .6 }} />
              <Input label="VAPI_VOICE_ID (default: rachel)" value="rachel" disabled style={{ opacity: .6 }} />
              <Input label="VAPI_VOICE_PROVIDER (default: elevenlabs)" value="elevenlabs" disabled style={{ opacity: .6 }} />
            </div>
          </Section>
          <Section title="🤖 How AI Calling Works — Per Company">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {[
                { co: 'Maven Group Global', color: '#00d4ff', type: 'BDM only', script: 'Agent introduces Maven Group, asks about software dev needs, qualifies interest, offers Calendly link to book a 15-min discovery call.' },
                { co: 'B2techsoftLLC',      color: '#7c3aed', type: 'BDM + Recruiting', script: 'BDM mode: Qualify interest in enterprise IT implementations. Recruiting mode: Check bench availability for SAP/Salesforce/Java roles, discuss C2C rates.' },
                { co: 'Xolosoft LLC',       color: '#00e676', type: 'Recruiting only', script: 'Check if consultant is on bench, confirm skill set and availability, ask about rate expectations, confirm remote-only or location preference.' },
              ].map(item => (
                <div key={item.co} style={{ background: 'var(--surface)', borderRadius: 8, padding: 14, borderLeft: `3px solid ${item.color}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                    <span style={{ fontWeight: 700, fontSize: 13, color: item.color }}>{item.co}</span>
                    <span style={{ fontSize: 11, background: item.color + '22', color: item.color, border: `1px solid ${item.color}44`, borderRadius: 4, padding: '2px 8px' }}>{item.type}</span>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--mid)', lineHeight: 1.7 }}>{item.script}</div>
                </div>
              ))}
            </div>
          </Section>
          <Section title="💰 Vapi.ai Pricing">
            <div style={{ fontSize: 13, color: 'var(--mid)', lineHeight: 1.9 }}>
              • Free tier: 10 minutes/month<br/>
              • Pay-as-you-go: ~$0.05-0.10/minute (voice + LLM combined)<br/>
              • 100 calls × 1.5 min avg = ~150 minutes = ~$10-15/month<br/>
              • Phone number: $2-5/month for US number<br/>
              • <strong>Estimated monthly cost: $15-20 for 100 AI calls</strong>
            </div>
          </Section>
        </>
      )}
    </div>
  );
}

export default Sequences;
